import React, { useState, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { 
  Edit2, Plus, Package, Check, X, Settings2
} from 'lucide-react';
import CustomOptionsConfig from './CustomOptionsConfig';
import LivePreviewPanel from './custom-options/LivePreviewPanel';

/**
 * CustomOptionsModal - Enhanced modal with two-panel layout
 * 
 * Left Panel: Option editor with choices
 * Right Panel: Live customer preview (light background)
 */
const CustomOptionsModal = ({
  formData,
  handleChange,
  schema,
  cardConfig = {}
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [localConfig, setLocalConfig] = useState({ options: [], hasOptions: false });
  
  const listingType = schema?.listingType || 'product';
  const optionsLabel = 'Custom Options';
  
  // Get current options from form data
  const customOptionsConfig = formData.customOptionsConfig || formData.productOptionsConfig || { options: [], hasOptions: false };
  const options = customOptionsConfig.options || [];
  const hasOptions = options.length > 0;
  
  // Calculate summary stats
  const totalOptions = options.length;

  // Handle opening modal - sync local config
  const handleOpenModal = () => {
    setLocalConfig(customOptionsConfig);
    setIsOpen(true);
  };

  const handleConfigChange = useCallback((config) => {
    setLocalConfig(config);
  }, []);

  const handleSave = () => {
    // Save to both old and new field names for compatibility
    handleChange('customOptionsConfig', localConfig);
    handleChange('productOptionsConfig', localConfig);
    handleChange('hasCustomOptions', localConfig.hasOptions || false);
    handleChange('hasProductOptions', localConfig.hasOptions || false);
    setIsOpen(false);
  };

  const handleCancel = () => {
    setLocalConfig(customOptionsConfig);
    setIsOpen(false);
  };

  // Calculate price breakdown for preview
  const calculatePreviewTotal = () => {
    const basePrice = parseFloat(formData.price) || 0;
    let addOnsTotal = 0;
    
    localConfig.options?.forEach(opt => {
      if (opt.mechanism === 'addon') {
        // For add-ons, sum selected choices
        opt.choices?.forEach(choice => {
          if (choice.selected) {
            addOnsTotal += parseFloat(choice.price) || 0;
          }
        });
      }
    });
    
    return { basePrice, addOnsTotal, total: basePrice + addOnsTotal };
  };

  return (
    <>
      {/* Summary Card - Shown in Form */}
      <Card className={`transition-all duration-200 ${hasOptions ? 'border-blue-200' : 'border-dashed'}`}>
        <CardHeader className="py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Settings2 className="h-5 w-5 text-blue-600" />
              <div>
                <CardTitle className="text-base">
                  {cardConfig.title || optionsLabel}
                </CardTitle>
                {!hasOptions && cardConfig.description && (
                  <p className="text-xs text-gray-500 mt-0.5">{cardConfig.description}</p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              {hasOptions && (
                <Badge variant="secondary" className="text-xs">
                  {totalOptions} option{totalOptions !== 1 ? 's' : ''}
                </Badge>
              )}
              <Button
                type="button"
                variant={hasOptions ? "outline" : "default"}
                size="sm"
                onClick={handleOpenModal}
                data-testid="edit-custom-options-btn"
              >
                {hasOptions ? (
                  <>
                    <Edit2 className="h-3 w-3 mr-1" />
                    Edit
                  </>
                ) : (
                  <>
                    <Plus className="h-3 w-3 mr-1" />
                    Add Options
                  </>
                )}
              </Button>
            </div>
          </div>
        </CardHeader>
        
        {/* Summary when has options */}
        {hasOptions && (
          <CardContent className="pt-0 pb-4">
            <div className="space-y-2">
              {options.slice(0, 3).map((option, idx) => (
                <div key={option.id || idx} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-2">
                    <Badge variant={option.mechanism === 'addon' ? 'outline' : 'secondary'} className="text-xs">
                      {option.mechanism === 'addon' ? 'Add-on' : 'Single Select'}
                    </Badge>
                    <p className="text-sm font-medium text-gray-900">{option.name || 'Unnamed Option'}</p>
                  </div>
                  <p className="text-xs text-gray-500">
                    {option.choices?.length || 0} choices
                  </p>
                </div>
              ))}
              {options.length > 3 && (
                <p className="text-xs text-gray-500 text-center">
                  +{options.length - 3} more option{options.length - 3 !== 1 ? 's' : ''}
                </p>
              )}
            </div>
          </CardContent>
        )}

        {/* Empty state */}
        {!hasOptions && (
          <CardContent className="pt-0 pb-4">
            <div className="bg-gray-50 rounded-lg p-3 border border-dashed border-gray-200 text-center">
              <Package className="h-6 w-6 mx-auto mb-2 text-gray-400" />
              <p className="text-xs text-gray-500">
                {listingType === 'service' 
                  ? 'Add extras like express service, premium products'
                  : listingType === 'rental'
                    ? 'Add extras like insurance, delivery, setup'
                    : 'Add options like gift wrapping, engraving, personalization'}
              </p>
            </div>
          </CardContent>
        )}
      </Card>

      {/* Modal with Two-Panel Layout - Extra Wide */}
      <Dialog open={isOpen} onOpenChange={(open) => !open && handleCancel()}>
        <DialogContent className="max-w-[1400px] w-[98vw] max-h-[92vh] overflow-hidden p-0">
          <DialogHeader className="px-6 pt-5 pb-3 border-b">
            <DialogTitle className="flex items-center gap-2">
              <Settings2 className="h-5 w-5 text-blue-600" />
              {cardConfig.title || `Manage ${optionsLabel}`}
            </DialogTitle>
            <DialogDescription>
              {cardConfig.description || `Add customizable options that customers can choose from when purchasing.`}
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex flex-col lg:flex-row flex-1 overflow-hidden" style={{ maxHeight: 'calc(92vh - 160px)' }}>
            {/* Left Panel - Option Editor (full width on mobile/tablet, 55% on desktop) */}
            <div className="w-full lg:w-[55%] overflow-y-auto p-4 md:p-6 border-b lg:border-b-0 lg:border-r">
              <CustomOptionsConfig
                key={isOpen ? 'open' : 'closed'}
                categoryId={formData.categoryId || formData.category_id || formData.category}
                subcategoryId={formData.subcategoryId || formData.subcategory_id || formData.subcategory}
                listingId={formData.id}
                basePrice={formData.price || formData.basePrice || 0}
                onConfigChange={handleConfigChange}
                initialOptions={customOptionsConfig.options || []}
              />
            </div>
            
            {/* Right Panel - Live Preview (hidden on mobile/tablet, 45% on desktop) */}
            <div className="hidden lg:flex lg:w-[45%]">
              <LivePreviewPanel localConfig={localConfig} formData={formData} calculatePreviewTotal={calculatePreviewTotal} />
            </div>
          </div>
          
          <DialogFooter className="px-6 py-4 border-t bg-gray-50">
            <Button type="button" variant="outline" onClick={handleCancel}>
              <X className="h-4 w-4 mr-1" />
              Cancel
            </Button>
            <Button type="button" onClick={handleSave} data-testid="save-custom-options-btn">
              <Check className="h-4 w-4 mr-1" />
              Save Options
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CustomOptionsModal;
